import React from 'react'
import PropTypes from 'prop-types'
import {
  Grid,
  Typography,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { setDecimalPrecision } from 'utils/helpers'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { ROUNDING_PRECISIONS } from 'utils/constants'

const NumberTableCell = styled(TableCell)(() => ({
  paddingLeft: '8px',
  paddingRight: '8px',
}))

const renderSummaryRows = ({ tickets, taxRate, showWeightDecimals, pricePrepend = '$' }) => {
  const { subtotal, netWeight } = tickets.reduce(
    (aggregate, ticket) => ({
      subtotal: aggregate.subtotal + (ticket.subTotal || 0),
      netWeight: aggregate.netWeight + (ticket.loadWeight?.net || ticket.netWeight || 0),
    }),
    { subtotal: 0, netWeight: 0 },
  )

  const printTaxValue = roundTo((subtotal * taxRate) / 100, ROUNDING_PRECISIONS.COST)
  const printTotalValue = subtotal + printTaxValue

  return (
    <>
      {tickets.map(ticket => (
        <TableRow key={`${ticket.id}-summary`}>
          <TableCell>{ticket.ticketId}</TableCell>
          <TableCell>{ticket.orderNumber || '-'}</TableCell>
          <TableCell>
            {ticket.materialTypeCommonNames?.length > 1 ? 'Mixed' : ticket.materialTypeCommonNames?.[0] || ''}
          </TableCell>
          <NumberTableCell align="right">
            {setDecimalPrecision(ticket.loadWeight?.net || ticket.netWeight, {
              removeTrailingZeros: true,
              decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
            })}
          </NumberTableCell>
          <NumberTableCell align="right">
            {pricePrepend}
            {setDecimalPrecision(ticket.freightCost?.cost, { decimalPrecisionOverride: 2 })}
          </NumberTableCell>
        </TableRow>
      ))}
      {printTaxValue ? (
        <>
          <TableRow key="subtotal-summary">
            <TableCell>Subtotal</TableCell>
            <TableCell />
            <TableCell />
            <NumberTableCell align="right">
              {setDecimalPrecision(netWeight, {
                removeTrailingZeros: true,
                decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
              })}
            </NumberTableCell>
            <NumberTableCell align="right">
              {pricePrepend}
              {setDecimalPrecision(subtotal, { decimalPrecisionOverride: 2 })}
            </NumberTableCell>
          </TableRow>
          <TableRow key="tax-summary">
            <TableCell>Tax</TableCell>
            <TableCell />
            <TableCell />
            <NumberTableCell align="right" />
            <NumberTableCell align="right">
              {pricePrepend}
              {setDecimalPrecision(printTaxValue, { decimalPrecisionOverride: 2 })}
            </NumberTableCell>
          </TableRow>
        </>
      ) : null}
      <TableRow key="total-summary" sx={{ backgroundColor: 'lightgray' }}>
        <TableCell sx={{ fontWeight: 600 }}>TOTAL</TableCell>
        <TableCell />
        <TableCell />
        <NumberTableCell sx={{ fontWeight: 600 }} align="right">
          {setDecimalPrecision(netWeight, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })}
        </NumberTableCell>
        <NumberTableCell sx={{ fontWeight: 600 }} align="right">
          {pricePrepend}
          {setDecimalPrecision(printTotalValue, { decimalPrecisionOverride: 2 })}
        </NumberTableCell>
      </TableRow>
      <TableRow sx={{ height: '32px' }} />
    </>
  )
}

const PrintTicketsFreightSummary = ({ title, headers, tickets, taxRate, showWeightDecimals, pricePrepend }) => (
  <Grid>
    <Stack direction="row" mb={1}>
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>
    </Stack>

    <TableContainer sx={{ overflow: 'hidden' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {headers.map((header, index) =>
              index > 2 ? (
                <NumberTableCell sx={{ fontWeight: 600 }} align="right" key={header}>
                  {header}
                </NumberTableCell>
              ) : (
                <TableCell sx={{ fontWeight: 600 }} align="left" key={header}>
                  {header}
                </TableCell>
              ),
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {renderSummaryRows({
            tickets,
            taxRate,
            showWeightDecimals,
            pricePrepend,
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </Grid>
)

PrintTicketsFreightSummary.propTypes = {
  title: PropTypes.string,
  headers: PropTypes.array,
  tickets: PropTypes.array,
  taxRate: PropTypes.number,
  showWeightDecimals: PropTypes.bool,
  pricePrepend: PropTypes.string,
}

PrintTicketsFreightSummary.defaultProps = {
  title: '',
  headers: [],
  tickets: [],
  taxRate: 0,
  showWeightDecimals: false,
  pricePrepend: '$',
}

export default PrintTicketsFreightSummary
